import React, {useEffect, useRef, useState} from "react";
import {
    KatButton,
    KatFlashbar,
    KatSpinner,
    KatTable,
    KatTableBody,
    KatTableCell,
    KatTableHead,
    KatTableRow,
} from "@amzn/katal-react";
import {Link, useHistory, useLocation, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {usernameSelector} from "src/control/selectors/commons/user_selectors";
import {ErrorContainer, ErrorHeading} from "src/view/style/modelTraining/errors";
import ExperimentsLogo from "src/view/images/Experiments-logo.png"
import {TableHeader} from "src/view/style/modelTraining/table_styles";
import {filterList, sortList} from "src/utils/list_utils";
import {PaddedSpan, TableRow, TableRowWithShadow} from "src/view/style/table_styles";
import {EditIconTable, ToggleOff, ToggleOn, ViewIconTable} from "src/view/style/icons";
import {listRunways, updateRunwayStatus} from "src/control/actions/modelTraining/runway_actions";
import {listRunwaysSelector, namespaceSelector} from "src/control/selectors/modelTraining/model_training_selectors";
import {
    getParamFromQueryString,
    removeTrailingSlashes, resetParamsQueryString,
    setParamToQueryString
} from "src/utils/url_utils";
import ReactTooltip from "react-tooltip";
import {PopUp} from "src/view/style/common_styles";
import {isEmpty, isValidMcmOrSev2Link, setIfMounted} from "src/utils/common_utils";
import {
    RunwayStatus,
    Stages,
    switchRunwayStatus,
    UPDATE_RUNWAY_STATUS_FAILURE_MESSAGE,
    UPDATE_RUNWAY_STATUS_SUCCESS_MESSAGE
} from "src/view/modelTraining/constants";
import {VInput, VKey, VStatusIndicator, VValue} from "src/view/style/view_config_styles";
import {Tab, Tabs} from "src/view/style/header_styles";
import {Domains} from "src/view/configPanel/config_constants";
import {showSnackBar} from "src/control/actions/commons/snack_bar_actions";

const ListRunways = () => {

    const dispatch = useDispatch();
    let userId = useSelector(usernameSelector);
    let listRunwaysData = useSelector(listRunwaysSelector);
    const fetchedNamespace = useSelector(namespaceSelector);
    const [mcmOrSev2, setMcmOrSev2] = useState("");
    const [isMCMOrSev2LinkValid, setIsMCMOrSev2LinkValid] = useState(false);


    const history = useHistory();
    const location = useLocation();
    const path = removeTrailingSlashes(location.pathname);

    const [enablePopUpVisible, setEnablePopUpVisible] = useState(false);
    const [selectedRunway, setSelectedRunway] = useState({
        runwayStatus: RunwayStatus.DISABLED,
        runwayId: "",
        domain: ""
    });
    const domainFromQueryString = getParamFromQueryString(location.search, "domain");
    const [domain, setDomain]: any = useState(domainFromQueryString);

    const domains = [Stages.EXPERIMENTATION.toUpperCase(), Stages.GAMMA.toUpperCase(), Stages.PROD.toUpperCase()]

    let _isMounted = useRef(true);
    const params: any = useParams()
    const resetQueryParams = useRef(false);

    useEffect(() => {
        _isMounted.current = true;
    });

    useEffect(() => {
        return () => {
            _isMounted.current = false;
        }
    });

    useEffect(() => {
        if (resetQueryParams.current)
            resetParamsQueryString();
    }, [resetQueryParams])

    useEffect(() => {
        if(!isEmpty(selectedRunway)){
            let domain = selectedRunway ? selectedRunway.domain : Domains.PROD;
            setIfMounted(_isMounted.current, setIsMCMOrSev2LinkValid, validateUpdatePrerequisites(mcmOrSev2, domain))
        }
    }, [selectedRunway])

    const loadRunwaysResult = () => {
        dispatch(listRunways(params.namespaceId))
    }

    const setDomainAndQueryParam = (domain: string) => {
        setParamToQueryString("domain", domain);
        setIfMounted(_isMounted.current, setDomain, domain);
    }

    const initializeDomain = () => {
        if (domainFromQueryString) {
            setDomainAndQueryParam(domainFromQueryString as string);
        } else {
            setDomainAndQueryParam(domains[0]);
        }
    }

    useEffect(() => {
        if (_isMounted.current) {
            loadRunwaysResult()
            initializeDomain()
        }
    }, [userId, params.teamId, params.namespaceId, dispatch]);

    const handleRedirectToView = (runwayId: string) => {
        history.push(
            `${path}/${runwayId}/view`
        );
    }

    const handleSelectDomain = (domain: string) => {
        setDomainAndQueryParam(domain);
    }

    const enableDisableRunway = (status: string) => {
        if (isProdDomain(selectedRunway.domain) && !isMCMOrSev2LinkValid) {
            dispatch(showSnackBar("Please provide a valid MCM/Sev2 link to continue"))
        }
        else {
            dispatch(updateRunwayStatus(selectedRunway.runwayId, status))
        }
    }

    const showEnableMessage = (listRunwaysData.updateRunwayStatusSuccess || listRunwaysData.updateRunwayStatusFailure)

    const getUpdateRunwayStatus = () => {
        if (listRunwaysData.updateRunwayStatusSuccess) {
            return UPDATE_RUNWAY_STATUS_SUCCESS_MESSAGE
        } else if (listRunwaysData.updateRunwayStatusFailure) {
            return UPDATE_RUNWAY_STATUS_FAILURE_MESSAGE
        }
        return ""
    }

    const isProdDomain = (domain: string) => {
        return (Domains.PROD === domain.toUpperCase());
    }

    const validateUpdatePrerequisites = (mcmOrSev2: string, domain: string) => {
        return isProdDomain(domain) ? isValidMcmOrSev2Link(mcmOrSev2) : true;
    }

    const getMCMOrSev2Row = () => {
        return (
            <TableRow>
                <VKey>MCM/Sev2 Link *

                    <VStatusIndicator
                        variant={isMCMOrSev2LinkValid? "success": "error"}
                        label={""}
                    />
                </VKey>
                <VValue>
                    <VInput
                        required
                        value={mcmOrSev2}
                        placeholder={"MCM/Sev2 for Updating"}
                        onInput={(e: any)=>{
                            setIfMounted(
                                _isMounted.current,
                                setMcmOrSev2,
                                e.target.value
                            )
                            setIfMounted(
                                _isMounted.current,
                                setIsMCMOrSev2LinkValid,
                                validateUpdatePrerequisites(e.target.value, selectedRunway.domain)
                            )
                        }}
                    />
                </VValue>
            </TableRow>
        )
    }

    const getInfrastructureInfoPopUp = () => {
        const buttonLabel: string = (selectedRunway.runwayStatus == RunwayStatus.DISABLED ? "Enable Runway" : "Disable Runway")
        const runwayStatus: string = switchRunwayStatus(selectedRunway.runwayStatus)

        return (
            <>
                <PopUp visible={enablePopUpVisible}
                       onOpen={(event: any) => {
                           setEnablePopUpVisible(true)
                       }}
                       onClose={(event: any) => {
                           setEnablePopUpVisible(false)
                       }}
                >
                    <span slot={"title"}>{buttonLabel}</span>
                    <p>
                        Are you sure you want to change runway status to {runwayStatus}
                    </p>
                    <div slot="footer" className="kat-group-horizontal">
                        <KatButton label={buttonLabel} size="base" variant="primary" onClick={(event: any) => {
                            enableDisableRunway(runwayStatus)
                            setIfMounted(_isMounted.current, setEnablePopUpVisible, false)
                        }}
                        />
                    </div>
                    {isProdDomain(selectedRunway.domain) && getMCMOrSev2Row()}
                </PopUp>
            </>
        );
    }

    const handleViewLogsMetrics = (stage: string, runwayId: string) => {
        const accountId: string = fetchedNamespace.namespaceData.accountTypeMap[stage]
        const dashboardName: string = `expresso-${runwayId}-modelTraining`
        const dashboardUrl: string = constructCloudWatchDashboardLink(accountId, dashboardName)
        window.open(dashboardUrl, '_blank')?.focus()
    }

    const constructCloudWatchDashboardLink = (accountId: string, dashboardName: string) => {
        return `https://cw-dashboards.aka.amazon.com/cloudwatch/dashboardInternal?accountId=${accountId}#dashboards:name=${dashboardName}`
    }

    const getRow = (runway: any) => {
        return (
            <TableRowWithShadow key={runway.runwayName}>
                <KatTableCell>{runway.runwayName}</KatTableCell>
                <KatTableCell>{runway.runwayId}</KatTableCell>
                <KatTableCell>{runway.createdBy}</KatTableCell>
                <KatTableCell>{runway.creationTime}</KatTableCell>
                <KatTableCell>
                    <ReactTooltip place="bottom" type="dark"/>
                    <PaddedSpan>
                        <Link
                            to={`/modelTraining/${params.teamId}/namespaces/${params.namespaceId}/runways/${runway.runwayId}/experiments`}>
                            <img data-tip="Experiments" style={{width: "35px", height: "35px"}} alt="Experiments"
                                 src={ExperimentsLogo}/>
                        </Link>
                    </PaddedSpan>
                    {
                        runway.domain.toUpperCase() != Stages.EXPERIMENTATION.toUpperCase() ?
                            <>
                                <PaddedSpan>
                                    <Link
                                        to={`/modelTraining/${params.teamId}/namespaces/${params.namespaceId}/runways/${runway.runwayId}/${runway.version}/update`}>
                                        <EditIconTable data-tip="Edit Runway"/>
                                    </Link>
                                </PaddedSpan>
                                <PaddedSpan data-tip="View Runway Details" onClick={(event: any) => {
                                    handleRedirectToView(runway.runwayId)
                                }}>
                                    <ViewIconTable/>
                                </PaddedSpan>
                                <PaddedSpan>
                                    <Link
                                        to={`/modelTraining/${params.teamId}/namespaces/${params.namespaceId}/runways/${runway.runwayId}/executions`}>
                                        <KatButton label="View Executions" size="small" variant="primary" onClick={(e: any) => {
                                            resetQueryParams.current = true;
                                            history.push(`/modelTraining/${params.teamId}/namespaces/${params.namespaceId}/runways/${runway.runwayId}/executions`)
                                        }}/>
                                    </Link>
                                </PaddedSpan>
                                <PaddedSpan>
                                        <KatButton label="View Logs/Metrics" size="small" variant="primary"
                                        onClick= {() => handleViewLogsMetrics(runway.domain.toLowerCase(), runway.runwayId)}/>
                                </PaddedSpan>
                            </> :
                            null
                    }
                </KatTableCell>
                {
                    runway.domain.toUpperCase() != Stages.EXPERIMENTATION.toUpperCase()?
                    <KatTableCell>
                        <div className={"row"} data-tip="Enable/disable runway" onClick={
                            () => {
                                setIfMounted(_isMounted.current, setSelectedRunway, runway)
                                setIfMounted(_isMounted.current, setEnablePopUpVisible, true)
                            }
                        }
                        >
                            {
                                runway.runwayStatus == RunwayStatus.ENABLED ?
                                    <div>

                                        <div className={"custom-toggle"}>
                                            <VStatusIndicator variant="success" label="Enabled"/>
                                            &nbsp;&nbsp;
                                            <ToggleOn/>
                                        </div>
                                    </div>
                                    :
                                    <div>
                                        <div className={"custom-toggle"}>
                                            <VStatusIndicator variant="error" label="Disabled"/>
                                            &nbsp;&nbsp;
                                            <ToggleOff/>
                                        </div>
                                    </div>
                            }
                        </div>
                    </KatTableCell>:
                        null
                }
            </TableRowWithShadow>
        )
    }

    return (
        <>
            {getInfrastructureInfoPopUp()}
            <div>
                {showEnableMessage ?
                    <KatFlashbar variant={listRunwaysData.updateRunwayStatusSuccess ? "success" : "danger"}
                                 header={getUpdateRunwayStatus()}
                    >
                    </KatFlashbar>
                    : listRunwaysData.startUpdateRunwayStatusLoader ? <KatSpinner/> : null
                }
            </div>
            <p>
                <Link style={{margin: "10px", float: "right"}}
                      to={`/modelTraining/${params.teamId}/namespaces/${params.namespaceId}/runways/create`}>
                    <KatButton label="Create New" size="small" variant="primary"/>
                </Link>
            </p>
            {
                (
                    listRunwaysData.runwaysFetched && !listRunwaysData.fetchAllRunwaysSuccess) ?
                    <ErrorContainer>
                        <ErrorHeading>Error loading runways</ErrorHeading>
                    </ErrorContainer>
                    :
                    <>
                        <div>
                            <TableHeader>
                                Runways
                            </TableHeader>
                        </div>
                        <Tabs selected={domain}>
                            {
                                // TODO: Remove this logic once the API supports query based on domain
                                domains.map((domain: string, index: number) => {
                                    const filterRunways = filterList(listRunwaysData.runwaysList, "domain", domain)
                                    return (
                                        <Tab key={index} tabId={domain} label={domain}
                                             onClick={(event) => handleSelectDomain(domain)}>
                                            {
                                                <KatTable>
                                                    {
                                                        (listRunwaysData.runwaysFetched && listRunwaysData.fetchAllRunwaysSuccess) ?
                                                            (filterRunways.length == 0) ?
                                                                    <ErrorContainer>
                                                                        <ErrorHeading>No Results Found</ErrorHeading>
                                                                    </ErrorContainer> :
                                                                    <>
                                                                        <KatTableHead>
                                                                            <KatTableRow>
                                                                                <KatTableCell>Name</KatTableCell>
                                                                                <KatTableCell>Id</KatTableCell>
                                                                                <KatTableCell>Created By</KatTableCell>
                                                                                <KatTableCell>Creation
                                                                                    Date</KatTableCell>
                                                                                <KatTableCell>Actions</KatTableCell>
                                                                                {
                                                                                    domain != Stages.EXPERIMENTATION.toUpperCase() ?
                                                                                        <KatTableCell>Status</KatTableCell> : null
                                                                                }
                                                                            </KatTableRow>
                                                                        </KatTableHead>
                                                                        <KatTableBody>
                                                                            {sortList(filterRunways, "runwayName").map(getRow)}
                                                                        </KatTableBody>
                                                                    </>
                                                            : <KatSpinner/>
                                                    }
                                                </KatTable>
                                            }
                                        </Tab>
                                    )
                                })
                            }
                        </Tabs>
                    </>
            }
        </>
    );
}

export default ListRunways